



























import { Component, Watch } from 'vue-property-decorator';
import { Route } from 'vue-router';
import {UserProfile} from "@api/models/UserProfile";
import {User} from "@api/models/User";
import { escapeHtml } from '@common/MessageProcessor';
import { Modal } from "@common/Modal";
import { SettingsHelper } from "../../helpers/settingsHelper"
import SettingsForm from "./SettingsForm.vue"
import PocketBlock from "@share/common/pocket/Pocket.vue";
import BounceSpinner from "@share/common/BounceSpinner.vue";
import Overlay from "@share/common/Overlay.vue";

import UserFormBaseOwn from "@service/components/user-form-base/user-form-base-own/UserFormBaseOwn.vue";
import SettingsHeader from "@service/components/settings/components/header/SettingsHeader.vue";
import { InfoBlocksEnum } from '@service/types/InfoBlocksEnum';
import {TooltipDirective} from "@share/directives/Tooltip";

@Component({
    name: 'Settings',
    components: { SettingsForm, PocketBlock, SettingsHeader, BounceSpinner, Overlay },
    directives: {TooltipDirective}
})
export default class Settings extends UserFormBaseOwn {
    closingInProccess = false;
    isShown = false;
    settingsHash = SettingsHelper.settingsHash;
    header: HTMLElement|null = null;

    InfoBlocksEnum = InfoBlocksEnum;

    @Watch('isShown')
    async onOpen(isShown: boolean) {
        if (isShown) {
            if (this.$route.hash != this.settingsHash) {
                window.location.hash = 'settings';
            }
            this.sendScreenView('Settings');
        }
        else {
            this.$clearAllBodyScrollLocks();
            this.sendScreenView(this.$store.state.screenNameData?.prevName || this.$route.meta?.analyticName);
        }

        this.cleanProfileFromHtmlTags();
        this.saveOriginDataString(this.profile, this.user);

        this.$emit("visibilityChange", isShown ? InfoBlocksEnum.settings : InfoBlocksEnum.empty)
    }

    async mounted() {
        //toggle from navbar
        this.$parent.$on('toggleSettings', this.toggle);
        this.$parent.$on('onboardingDone', this.loadData);

        if(window.location.hash === this.settingsHash) {
            this.isShown = true;
        }

        if(this.isShown) {
            await this.loadData();
        }

        this.$router.beforeEach(this.routingCallback);
        this.helper = new SettingsHelper(this.$store);
    }

    destroyed() {
        //toggle from navbar
        this.$parent.$off('toggleSettings', this.toggle);
        this.$parent.$off('onboardingDone', this.loadData);
    }

    async showAndLoadIfNeeded() {
        if(!this.isLoaded) {
            await this.loadData();
        }
        this.isShown = true;
    }

    async routingCallback(to: Route, from: Route, next: any) {

        if(to.name === "Login") {
            this.resetForm();
            this.isShown = false;
            next();
            return;
        }

        if (to.hash == this.settingsHash && !this.isShown) {
            await this.showAndLoadIfNeeded()
        }

        if(this.closingInProccess) {
            return;
        }

        //for closing
        if(to.hash !== this.settingsHash && this.isShown) {
            this.closingInProccess = true;
            try {
                const closed = await this.tryClose(false);

                if(closed) {
                    next();
                }

                //if setings can't be close, than prevent routing
            }
            catch(e) {
                this.closingInProccess = false;
            }

            this.closingInProccess = false;

        }
        else {
            next()
        }
    }

    toggle() {
        if(!this.isShown) {
            this.showAndLoadIfNeeded();
        }
        else {
            this.tryClose();
        }
    }

    async tryClose(withBack = true): Promise<boolean> {

        const cantBeClosed = this.helper && !this.helper.objectSameAsString({profile: this.profile, user: this.user }, this.originUserDataString) && this.originUserDataString;

        if(cantBeClosed) {

            const confirm = await this.showConfirmation();

            //modal will close by x button
            if(confirm === "close") {
                if(this.settingsHash !== this.$route.hash) {
                    this.$router.push({
                        ...this.$route,
                        name: this.$route.name || 'Home',
                        hash: this.settingsHash
                    })
                }

                this.showAndLoadIfNeeded();
                return false;
            }

            //data save confirmed
            if(confirm === true) {
                //false since we are trying to close the window
                if(await this.save(false) === false) {
                    return false;
                }
            }
            else {
                this.resetForm();
            }
        }

        this.isShown = false;

        if(withBack) {
            this.$router.push({path: this.$route.path})
        }

        return true;
    }

    resetForm() {
        const { profile, user } = this.helper?.getObjectFromString(this.originUserDataString);

        if(profile && user) {
            this.profile = new UserProfile(profile);
            this.user = new User(user);
            this.validationMessages = {};

            this.saveOriginDataString(this.profile, this.user);
        }
    }

    async showConfirmation() {
        const modal = new Modal({
            title: this.$t("settings.dataNotSaved").toString(),
            contentText: this.$t("settings.saveChanges").toString() + "?",
            buttons: [
                {
                    text: this.$t("common.save").toString(),
                    class: "is-green",
                    returnValue: true,
                },
                {
                    text: this.$t("common.cancel changes").toString(),
                    class: "is-red is-outlined",
                    returnValue: false,
                },
            ],
        });

        return (await modal.showConfirm()) as string | boolean;
    }

    getTop() {
        return (document.querySelector('nav')?.clientHeight ?? 0);
    }

    cleanProfileFromHtmlTags() {
        this.fields.forEach((field) => {
            if(field.type !== "tag") {
                const cleaned = escapeHtml(this.profile[field.name], [null]);

                if(['undefined', 'null'].includes(cleaned) || !cleaned) {
                    this.profile[field.name] = '';
                }
                else {
                    this.profile[field.name] = cleaned;
                }
            }
        });
    }

    get logoutTooltipData() {
        return this.$t('common.logout');
    }

    get deviceTestTooltipData() {
        return this.$t('web-conferences.deviceTest');
    }
}
